/**
 * `Sidebar` — slim left-rail nav for the admin SPA.
 *
 * The menu tree (top-level links + collapsible groups) lives in
 * `nav-config.ts`; this file only renders it. Groups remember their
 * open/closed state per viewer in localStorage, and the group holding
 * the current page opens itself on navigation.
 *
 * Responsive:
 *   - md+ (≥768px): persistent left rail (the dense default).
 *   - <md: a hamburger button (rendered in `AdminHeader` slot) toggles a
 *     fixed-position drawer that slides in from the left, full-height,
 *     with a scrim. Tapping a nav item closes the drawer.
 *
 * The mobile-toggle state is hoisted to the parent (App.tsx) so the
 * hamburger sits in the header but controls the drawer here, and an
 * outside click on the scrim closes it.
 */

import { ChevronRight, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { useC } from '../../lib/theme.js';
import type { SetView, View } from '../../app/view-router.js';
import { useAuth } from '../../app/auth-context.js';
import {
  activeGroupFor,
  activeItemFor,
  isGroupOpen,
  loadOpenState,
  navStorageKey,
  saveOpenState,
  visibleNav,
} from './nav-config.js';
import type { NavGroup, NavItem, OpenState } from './nav-config.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface SidebarProps {
  view: View;
  setView: SetView;
  /** Mobile drawer state — owned by <App/>. */
  mobileOpen: boolean;
  onMobileClose: () => void;
  /** Optional counts shown next to a link (e.g. pending approvals). A
   *  collapsed group shows the sum of its children's counts. */
  badges?: Partial<Record<View, number>>;
}

const NO_BADGES: Partial<Record<View, number>> = {};

export function Sidebar({
  view,
  setView,
  mobileOpen,
  onMobileClose,
  badges = NO_BADGES,
}: SidebarProps): JSX.Element {
  const C = useC();
  const auth = useAuth();
  const staff = auth.state.kind === 'authenticated' ? auth.state.staff : undefined;
  const role = staff?.role;
  // Only the pages this staff member's role can open; empty groups dropped.
  const nav = useMemo(() => visibleNav(role), [role]);
  const activeId = activeItemFor(view)?.id;
  const activeGroupId = activeGroupFor(view)?.id;

  const storageKey = navStorageKey(staff?.staff_id);
  const [openState, setOpenState] = useState<OpenState>(() => loadOpenState(storageKey));

  // Re-read when the signed-in staff member changes (per-viewer state).
  useEffect(() => {
    setOpenState(loadOpenState(storageKey));
  }, [storageKey]);

  // The group holding the current page opens on navigation. The viewer
  // can still collapse it afterwards while staying on the page.
  useEffect(() => {
    if (activeGroupId === undefined) return;
    setOpenState((prev) => {
      if (prev[activeGroupId] === true) return prev;
      const next = { ...prev, [activeGroupId]: true };
      saveOpenState(storageKey, next);
      return next;
    });
  }, [activeGroupId, storageKey]);

  const toggleGroup = (group: NavGroup): void => {
    setOpenState((prev) => {
      const next = { ...prev, [group.id]: !isGroupOpen(group, prev) };
      saveOpenState(storageKey, next);
      return next;
    });
  };

  const handleNavClick = (id: View): void => {
    setView(id);
    onMobileClose();
  };

  const renderTree = (large: boolean, idPrefix: string): JSX.Element => (
    <ul className="flex flex-col gap-1" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {nav.map((entry) =>
        entry.kind === 'item' ? (
          <li key={entry.id}>
            <NavButton
              item={entry}
              active={activeId === entry.id}
              badge={badges[entry.id]}
              onClick={(): void => handleNavClick(entry.id)}
              large={large}
            />
          </li>
        ) : (
          <li key={entry.id}>
            <NavGroupSection
              group={entry}
              open={isGroupOpen(entry, openState)}
              onToggle={(): void => toggleGroup(entry)}
              containsActive={activeGroupId === entry.id}
              activeId={activeId}
              badges={badges}
              onNavigate={handleNavClick}
              large={large}
              idPrefix={idPrefix}
            />
          </li>
        ),
      )}
    </ul>
  );

  return (
    <>
      {/* Desktop rail — md+ only */}
      <nav
        aria-label="Admin navigation"
        className="hidden md:flex flex-col w-56 flex-shrink-0 p-3 gap-1"
        style={{
          background: C.surface,
          borderRight: `1px solid ${C.borderSoft}`,
          minHeight: 'calc(100vh - 60px)',
        }}
      >
        <SidebarHeading C={C} />
        {renderTree(false, 'nav-rail')}
      </nav>

      {/* Mobile drawer — only when open, <md. Scrim + slide-in panel. */}
      {mobileOpen ? (
        <div
          className="md:hidden fixed inset-0 z-50"
          role="dialog"
          aria-modal="true"
          aria-label="Admin navigation"
        >
          {/* Scrim */}
          <div
            className="absolute inset-0"
            onClick={onMobileClose}
            style={{
              background: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(2px)',
            }}
            aria-hidden="true"
          />

          {/* Panel */}
          <nav
            aria-label="Admin navigation"
            className="absolute top-0 left-0 h-full flex flex-col p-3 gap-1 overflow-y-auto"
            style={{
              width: 260,
              maxWidth: '85vw',
              background: C.surface,
              borderRight: `1px solid ${C.border}`,
              boxShadow: '0 10px 40px rgba(0,0,0,0.45)',
            }}
          >
            <div className="flex items-center justify-between px-2 pt-1 pb-2">
              <SidebarHeading C={C} inline />
              <button
                type="button"
                onClick={onMobileClose}
                aria-label="Close navigation"
                className="flex items-center justify-center"
                style={{
                  width: 40,
                  height: 40,
                  background: 'transparent',
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  color: C.muted,
                }}
              >
                <X size={16} />
              </button>
            </div>
            {renderTree(true, 'nav-drawer')}
          </nav>
        </div>
      ) : null}
    </>
  );
}

function SidebarHeading({
  C,
  inline = false,
}: {
  C: ReturnType<typeof useC>;
  inline?: boolean;
}): JSX.Element {
  return (
    <div
      className={inline ? '' : 'px-2 pt-1 pb-2'}
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        color: C.dim,
        letterSpacing: 2,
        textTransform: 'uppercase',
      }}
    >
      Console
    </div>
  );
}

function NavGroupSection({
  group,
  open,
  onToggle,
  containsActive,
  activeId,
  badges,
  onNavigate,
  large,
  idPrefix,
}: {
  group: NavGroup;
  open: boolean;
  onToggle: () => void;
  containsActive: boolean;
  activeId: View | undefined;
  badges: Partial<Record<View, number>>;
  onNavigate: (id: View) => void;
  large: boolean;
  idPrefix: string;
}): JSX.Element {
  const C = useC();
  const panelId = `${idPrefix}-group-${group.id}`;
  const total = group.items.reduce((sum, i) => sum + (badges[i.id] ?? 0), 0);
  return (
    <div className="flex flex-col gap-1" style={{ marginTop: 6 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        data-nav-group={group.id}
        className="flex items-center gap-2 text-left w-full"
        style={{
          fontFamily: monoFont,
          fontSize: large ? 11 : 10,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          color: containsActive ? C.text : C.muted,
          background: 'transparent',
          border: '1px solid transparent',
          borderRadius: 2,
          padding: large ? '10px 12px' : '6px 12px',
          minHeight: large ? 44 : undefined,
          cursor: 'pointer',
        }}
      >
        <ChevronRight
          size={12}
          aria-hidden="true"
          color={containsActive ? C.limeFg : C.dim}
          style={{
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 120ms ease',
            flexShrink: 0,
          }}
        />
        <span className="flex-1">{group.label}</span>
        {!open && total > 0 ? <Badge count={total} /> : null}
      </button>
      <ul
        id={panelId}
        hidden={!open}
        style={{
          display: open ? 'flex' : 'none',
          flexDirection: 'column',
          gap: 4,
          listStyle: 'none',
          margin: 0,
          marginLeft: 13,
          padding: 0,
          paddingLeft: 8,
          borderLeft: `1px solid ${C.borderSoft}`,
        }}
      >
        {open
          ? group.items.map((item) => (
              <li key={item.id}>
                <NavButton
                  item={item}
                  active={activeId === item.id}
                  badge={badges[item.id]}
                  onClick={(): void => onNavigate(item.id)}
                  large={large}
                />
              </li>
            ))
          : null}
      </ul>
    </div>
  );
}

function Badge({ count }: { count: number }): JSX.Element {
  const C = useC();
  return (
    <span
      aria-label={`${count} pending`}
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        lineHeight: '14px',
        letterSpacing: 0,
        padding: '0 5px',
        borderRadius: 2,
        border: `1px solid ${C.limeFg}`,
        color: C.limeFg,
        background: C.limeDim,
        flexShrink: 0,
      }}
    >
      {count}
    </span>
  );
}

function NavButton({
  item,
  active,
  onClick,
  badge,
  large = false,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
  badge?: number | undefined;
  large?: boolean;
}): JSX.Element {
  const C = useC();
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-nav-item={item.id}
      className="flex items-center gap-2 text-left w-full"
      style={{
        fontFamily: sansFont,
        fontSize: large ? 14 : 13,
        fontWeight: 500,
        color: active ? C.text : C.muted,
        background: active ? C.surface2 : 'transparent',
        border: `1px solid ${active ? C.border : 'transparent'}`,
        // Lime single-accent marker on the active link.
        boxShadow: active ? `inset 2px 0 0 0 ${C.limeFg}` : undefined,
        borderRadius: 2,
        padding: large ? '10px 12px' : '7px 12px',
        minHeight: large ? 44 : undefined,
      }}
      aria-current={active ? 'page' : undefined}
    >
      <Icon size={large ? 16 : 14} color={active ? C.text : C.muted} />
      <span className="flex-1">{item.label}</span>
      {badge !== undefined && badge > 0 ? <Badge count={badge} /> : null}
    </button>
  );
}
